/**
 * Helpers for drag-and-drop moving of one officer's block of slots between
 * counters (same time range, different row).
 */
import type { CellData, HistorySnapshot } from "@/types/api";

export interface OfficerBlock {
  officerId: string;
  /** First slot of the block (inclusive). */
  startCol: number;
  /** Last slot of the block (inclusive). */
  endCol: number;
}

/**
 * The contiguous run of slots on `row` that `officerId` holds, through `col`.
 *
 * The run only ends where the officer is no longer on this counter — other
 * officers sharing (overlapping) those slots do NOT split it. `officerId`
 * names which officer to pick up in a shared cell; when omitted the cell must
 * hold exactly one officer, otherwise there is nothing unambiguous to pick up.
 */
export function findOfficerBlock(
  cells: CellData[],
  numSlots: number,
  row: number,
  col: number,
  officerId?: string,
): OfficerBlock | null {
  const at = (c: number) => cells[row * numSlots + c];
  const first = at(col);
  if (!first || first.officers.length === 0) return null;
  const id = officerId ?? (first.officers.length === 1 ? first.officers[0] : undefined);
  if (!id || !first.officers.includes(id)) return null;
  const holds = (c: number) => !!at(c)?.officers.includes(id);
  let startCol = col;
  while (startCol > 0 && holds(startCol - 1)) startCol--;
  let endCol = col;
  while (endCol < numSlots - 1 && holds(endCol + 1)) endCol++;
  return { officerId: id, startCol, endCol };
}

/** Other officers already on `targetRow` anywhere inside the block's slot range. */
export function targetConflicts(
  cells: CellData[],
  numSlots: number,
  targetRow: number,
  block: OfficerBlock,
): string[] {
  const found = new Set<string>();
  for (let c = block.startCol; c <= block.endCol; c++) {
    for (const id of cells[targetRow * numSlots + c]?.officers ?? []) {
      if (id !== block.officerId) found.add(id);
    }
  }
  return [...found];
}

export interface NoteRun {
  startCol: number;
  /** Inclusive. */
  endCol: number;
  note?: string;
}

/**
 * Split the block into runs that share the same note, so moving it doesn't
 * flatten per-slot notes into one (an allocation carries a single note).
 */
export function noteRuns(
  block: OfficerBlock,
  noteAt: (slot: number) => string | undefined,
): NoteRun[] {
  const runs: NoteRun[] = [];
  for (let c = block.startCol; c <= block.endCol; c++) {
    const note = noteAt(c) || undefined;
    const last = runs[runs.length - 1];
    if (last && last.note === note) last.endCol = c;
    else runs.push({ startCol: c, endCol: c, note });
  }
  return runs;
}

/**
 * The snapshot with `officerKey` moved from counter `from` to counter `to` (1-based) over
 * slots [startSlot, endSlot) — exactly what /officers/update_assignments will do for a
 * block move, applied locally so the grid shows it before the round trip returns. Only
 * slots where the officer is still on `from` move; everything else is left as it is.
 */
export function moveBlockInSnapshot(
  snap: HistorySnapshot,
  officerKey: string,
  from: number,
  to: number,
  startSlot: number,
  endSlot: number,
): HistorySnapshot {
  const schedule = snap.officer_schedules[officerKey];
  if (!schedule || from === to) return snap;
  const moved: number[] = [];
  for (let s = startSlot; s < endSlot && s < schedule.length; s++) if (schedule[s] === from) moved.push(s);
  if (moved.length === 0) return snap;
  const nextSchedule = [...schedule];
  for (const s of moved) nextSchedule[s] = to;
  // The server lists a slot's officers in officer_schedules order; match it, so the
  // grid drawn now equals the one the save returns and is not rebuilt when it lands.
  const rank = new Map(Object.keys(snap.officer_schedules).map((k, i) => [k, i]));
  const byRank = (a: string, b: string) => (rank.get(a) ?? Infinity) - (rank.get(b) ?? Infinity);
  const counter_matrix = snap.counter_matrix.map((slots, row) => {
    if (row !== from - 1 && row !== to - 1) return slots;
    const next = [...slots];
    for (const s of moved) {
      const here = (next[s] ?? []).filter((o) => o !== officerKey);
      next[s] = row === to - 1 ? [...here, officerKey].sort(byRank) : here;
    }
    return next;
  });
  return {
    ...snap,
    counter_matrix,
    officer_schedules: { ...snap.officer_schedules, [officerKey]: nextSchedule },
  };
}
